// src/pages/app/TravelRequestDetailPage.jsx — View/manage single travel request
import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { travelRequestsApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, XCircle, Upload, Send,
  Plane, Hotel, Car, Train, MapPin, Calendar, User,
  DollarSign, FileText, AlertTriangle, Loader2, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import FileUpload from '@/components/ui/FileUpload';
import { cn } from '@/lib/utils';

const STATUS_CONFIG = {
  draft: { color: 'bg-slate-100 text-slate-700', label: 'Draft' },
  pending_approval: { color: 'bg-amber-100 text-amber-800', label: 'Pending Approval' },
  approved: { color: 'bg-blue-100 text-blue-800', label: 'Approved' },
  booked: { color: 'bg-indigo-100 text-indigo-800', label: 'Booked' },
  rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
};

const SERVICE_ICONS = { flight: Plane, hotel: Hotel, cab: Car, train: Train };

export default function TravelRequestDetailPage() {
  const { id } = useParams();
  const { user, isRole } = useAuth();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [itinForm, setItinForm] = useState({ pnr: '', hotel_ref: '', cab_ref: '', notes: '', file_url: '' });
  const [showItinForm, setShowItinForm] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [rejectComment, setRejectComment] = useState('');

  const load = async () => {
    try {
      const { data } = await travelRequestsApi.get(id);
      setRequest(data);
    } catch { toast.error('Failed to load travel request'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleSubmit = async () => {
    setActionLoading('submit');
    try {
      await travelRequestsApi.submit(id);
      toast.success('Submitted for manager approval!');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Submit failed'); }
    finally { setActionLoading(''); }
  };

  const handleConfirmApprove = async () => {
    setActionLoading('approve');
    try {
      await travelRequestsApi.approve(id, { comment: approveComment });
      toast.success('Approved!');
      setShowApproveModal(false);
      setApproveComment('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
    finally { setActionLoading(''); }
  };

  const handleConfirmReject = async () => {
    if (!rejectComment || rejectComment.trim().length < 5) {
      toast.error('Rejection comment required (min 5 chars)');
      return;
    }
    setActionLoading('reject');
    try {
      await travelRequestsApi.reject(id, { comment: rejectComment });
      toast.success('Rejected');
      setShowRejectModal(false);
      setRejectComment('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rejection failed'); }
    finally { setActionLoading(''); }
  };

  const handleItinerary = async (e) => {
    e.preventDefault();
    setActionLoading('itinerary');
    try {
      await travelRequestsApi.uploadItinerary(id, itinForm);
      toast.success('Itinerary uploaded! Employee notified.');
      setShowItinForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setActionLoading(''); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  if (!request) return <div className="text-center py-16 text-slate-400">Request not found</div>;

  const statusConf = STATUS_CONFIG[request.status] || STATUS_CONFIG.draft;
  const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  // Stepper calculations
  const steps = [
    { key: 'draft', label: 'Draft Created' },
    { key: 'pending_approval', label: 'Manager Review' },
    { key: 'approved', label: 'Travel Desk' },
    { key: 'booked', label: 'Booked & Complete' },
  ];

  const isSuccess = request.status === 'booked';
  const isRejected = request.status === 'rejected';

  const currentStepIdx = isSuccess ? 3 :
    request.status === 'approved' ? 2 :
    request.status === 'pending_approval' ? 1 :
    request.status === 'draft' ? 0 : 1;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link to="/app/travel-requests" className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 mt-1">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">
              Trip to {request.destination}
            </h1>
            <span className={cn("px-3 py-1 rounded-full text-sm font-medium", statusConf.color)}>
              {statusConf.label}
            </span>
            {request.trip_type === 'international' && (
              <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">International</span>
            )}
          </div>
          <p className="text-slate-500 text-sm mt-1">
            Submitted by {request.employee_name} · Created {new Date(request.created_at).toLocaleDateString('en-IN')}
          </p>
        </div>
      </div>

      {/* Visual Workflow Stepper */}
      <Card className="border-slate-200 bg-white p-3 sm:p-4 lg:p-6 shadow-sm">
        <div className="relative max-w-2xl mx-auto px-4">
          {/* Base Gray Track Line: from center of Col 1 (12.5%) to center of Col 4 (87.5%) */}
          <div className="absolute left-[12.5%] right-[12.5%] top-4 -translate-y-1/2 h-1 bg-slate-200 z-0">
            {/* Active Filled Progress Line */}
            <div
              className={cn(
                "h-1 transition-all duration-500 rounded-full",
                request.status === 'rejected' ? "bg-rose-500" : "bg-emerald-500"
              )}
              style={{
                width: `${
                  request.status === 'booked' ? 100 :
                  request.status === 'approved' ? 66.666 :
                  request.status === 'pending_approval' || request.status === 'rejected' ? 33.333 : 0
                }%`
              }}
            />
          </div>

          <div className="relative flex items-start justify-between z-10">
            {/* Step 1: Draft Created */}
            <div className="flex-1 flex flex-col items-center text-center">
              <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold shadow-sm ring-4 ring-white">
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <span className="text-xs mt-2 font-bold text-emerald-700 whitespace-nowrap">
                Draft Created
              </span>
            </div>

            {/* Step 2: Manager Review */}
            <div className="flex-1 flex flex-col items-center text-center">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all shadow-sm ring-4 ring-white",
                request.status === 'rejected'
                  ? "bg-rose-600 text-white ring-4 ring-rose-100"
                  : ['approved', 'booked'].includes(request.status)
                  ? "bg-emerald-600 text-white"
                  : request.status === 'pending_approval'
                  ? "bg-blue-600 text-white ring-4 ring-blue-100 shadow-md"
                  : "bg-white text-slate-400 border-2 border-slate-200"
              )}>
                {request.status === 'rejected' ? (
                  <XCircle className="w-4 h-4" />
                ) : ['approved', 'booked'].includes(request.status) ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  "2"
                )}
              </div>
              <span className={cn(
                "text-xs mt-2 font-semibold whitespace-nowrap",
                request.status === 'rejected'
                  ? "text-rose-600 font-bold"
                  : ['approved', 'booked'].includes(request.status)
                  ? "text-emerald-700 font-bold"
                  : request.status === 'pending_approval'
                  ? "text-blue-700 font-bold"
                  : "text-slate-400"
              )}>
                {request.status === 'rejected' ? 'Manager Rejected' : 'Manager Review'}
              </span>
            </div>

            {/* Step 3: Travel Desk */}
            <div className="flex-1 flex flex-col items-center text-center">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all shadow-sm ring-4 ring-white",
                request.status === 'booked'
                  ? "bg-emerald-600 text-white"
                  : request.status === 'approved'
                  ? "bg-blue-600 text-white ring-4 ring-blue-100 shadow-md"
                  : "bg-white text-slate-400 border-2 border-slate-200"
              )}>
                {request.status === 'booked' ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  "3"
                )}
              </div>
              <span className={cn(
                "text-xs mt-2 font-semibold whitespace-nowrap",
                request.status === 'booked'
                  ? "text-emerald-700 font-bold"
                  : request.status === 'approved'
                  ? "text-blue-700 font-bold"
                  : "text-slate-400"
              )}>
                Travel Desk
              </span>
            </div>

            {/* Step 4: Booked & Complete */}
            <div className="flex-1 flex flex-col items-center text-center">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all shadow-sm ring-4 ring-white",
                request.status === 'booked'
                  ? "bg-emerald-600 text-white ring-4 ring-emerald-100 shadow-md"
                  : "bg-white text-slate-400 border-2 border-slate-200"
              )}>
                {request.status === 'booked' ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  "4"
                )}
              </div>
              <span className={cn(
                "text-xs mt-2 font-semibold whitespace-nowrap",
                request.status === 'booked' ? "text-emerald-700 font-bold" : "text-slate-400"
              )}>
                Booked & Complete
              </span>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Trip Info */}
          <Card>
            <CardHeader><CardTitle className="text-sm sm:text-base">Trip Information</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Route</p>
                  <p className="text-sm font-medium text-slate-900">{request.origin || 'N/A'} → {request.destination}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Dates</p>
                  <p className="text-sm font-medium text-slate-900">
                    {new Date(request.start_date).toLocaleDateString('en-IN')} – {new Date(request.end_date).toLocaleDateString('en-IN')}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Employee</p>
                  <p className="text-sm font-medium text-slate-900">{request.employee_name}</p>
                  <p className="text-xs text-slate-500">{request.employee_grade} · {request.employee_department}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <DollarSign className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide">Budget Estimate</p>
                  <p className="text-sm font-medium text-slate-900">
                    {request.budget_estimate ? `₹${parseFloat(request.budget_estimate).toLocaleString('en-IN')}` : 'Not specified'}
                  </p>
                  {request.budget_check_status === 'flagged' && (
                    <span className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
                      <AlertTriangle className="w-3 h-3" /> Budget flagged
                    </span>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Purpose</p>
                <p className="text-sm text-slate-700">{request.purpose}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Required Services</p>
                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(request.services) ? request.services : JSON.parse(request.services || '[]')).map(s => {
                    const Icon = SERVICE_ICONS[s] || Plane;
                    return (
                      <span key={s} className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium capitalize">
                        <Icon className="w-3 h-3" />{s}
                      </span>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Itinerary Display & Documents */}
          {request.itinerary && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" /> Booking Itinerary & Uploaded Tickets
                  </CardTitle>
                  {isRole('travel_desk', 'admin') && (
                    <button
                      onClick={() => {
                        const fileUrls = request.itinerary?.file_urls && request.itinerary.file_urls.length > 0
                          ? request.itinerary.file_urls
                          : (request.itinerary?.file_url ? [request.itinerary.file_url] : []);
                        setItinForm({
                          pnr: request.itinerary.pnr || '',
                          hotel_ref: request.itinerary.hotel_ref || '',
                          cab_ref: request.itinerary.cab_ref || '',
                          notes: request.itinerary.notes || '',
                          file_urls: fileUrls,
                        });
                        setShowItinForm(!showItinForm);
                      }}
                      className="text-xs px-3 py-1.5 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 font-semibold flex items-center gap-1.5"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {showItinForm ? 'Cancel Edit' : 'Update Tickets / Bills'}
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  {request.itinerary.pnr && (
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wide mb-1 font-semibold">Flight PNR</p>
                      <p className="text-sm font-mono font-bold text-slate-900 bg-slate-50 px-2.5 py-1 rounded-md border border-slate-200 inline-block">{request.itinerary.pnr}</p>
                    </div>
                  )}
                  {request.itinerary.hotel_ref && (
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wide mb-1 font-semibold">Hotel Ref</p>
                      <p className="text-sm font-mono font-bold text-slate-900 bg-slate-50 px-2.5 py-1 rounded-md border border-slate-200 inline-block">{request.itinerary.hotel_ref}</p>
                    </div>
                  )}
                  {request.itinerary.cab_ref && (
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wide mb-1 font-semibold">Cab Ref</p>
                      <p className="text-sm font-mono font-bold text-slate-900 bg-slate-50 px-2.5 py-1 rounded-md border border-slate-200 inline-block">{request.itinerary.cab_ref}</p>
                    </div>
                  )}
                </div>
                {request.itinerary.notes && (
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-1 font-semibold">Travel Desk Notes</p>
                    <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg border border-slate-200">{request.itinerary.notes}</p>
                  </div>
                )}
                {(() => {
                  const urls = request.itinerary.file_urls && request.itinerary.file_urls.length > 0
                    ? request.itinerary.file_urls
                    : (request.itinerary.file_url ? [request.itinerary.file_url] : []);
                  if (!urls.length) return null;

                  return (
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wide mb-2 font-semibold">
                        Uploaded Booking Bills / E-Tickets / Vouchers ({urls.length})
                      </p>
                      <div className="grid sm:grid-cols-2 gap-2.5">
                        {urls.map((u, i) => {
                          const getFullUrl = (urlStr) => {
                            if (urlStr.startsWith('http') || urlStr.startsWith('data:')) return urlStr;
                            const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                            return `${apiBase.replace(/\/api\/?$/, '')}${urlStr.startsWith('/') ? '' : '/'}${urlStr}`;
                          };
                          const fileName = u.split('/').pop().split('?')[0] || `Attachment ${i + 1}`;
                          const isPdf = u.toLowerCase().includes('.pdf') || u.startsWith('data:application/pdf');

                          return (
                            <div key={i} className="p-3 rounded-xl border border-blue-200 bg-blue-50/50 flex items-center justify-between gap-2 shadow-2xs">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className={cn(
                                  "w-9 h-9 rounded-lg flex items-center justify-center font-bold text-xs shrink-0",
                                  isPdf ? "bg-rose-100 text-rose-700 border border-rose-200" : "bg-blue-600 text-white"
                                )}>
                                  {isPdf ? 'PDF' : <FileText className="w-4 h-4" />}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-bold text-slate-900 truncate">{fileName}</p>
                                  <p className="text-[10px] text-slate-500 truncate mt-0.5 max-w-[200px]">{u}</p>
                                </div>
                              </div>
                              <a
                                href={getFullUrl(u)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold flex items-center gap-1 shrink-0 transition-colors shadow-2xs"
                              >
                                <ExternalLink className="w-3.5 h-3.5" /> View
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Upload / Update Itinerary Form (Travel Desk) */}
          {isRole('travel_desk', 'admin') && ['approved', 'booked'].includes(request.status) && (!request.itinerary || showItinForm) && (
            <Card className="border-blue-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                    <Upload className="w-4 h-4 text-blue-600" />
                    {request.itinerary ? 'Update Booking Tickets & Bills' : 'Upload Itinerary & Booking Details'}
                  </CardTitle>
                  {!request.itinerary && (
                    <button onClick={() => setShowItinForm(!showItinForm)} className="text-sm text-blue-600 hover:underline font-medium">
                      {showItinForm ? 'Cancel' : 'Add Details'}
                    </button>
                  )}
                </div>
              </CardHeader>
              {(!request.itinerary || showItinForm) && (
                <CardContent>
                  <form onSubmit={handleItinerary} className="space-y-4">
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Flight PNR</label>
                        <input value={itinForm.pnr} onChange={e => setItinForm(f => ({ ...f, pnr: e.target.value }))} placeholder="E.g. ABC123" className={inputCls} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Hotel Ref</label>
                        <input value={itinForm.hotel_ref} onChange={e => setItinForm(f => ({ ...f, hotel_ref: e.target.value }))} placeholder="E.g. HBK-9876" className={inputCls} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Cab/Transit Ref</label>
                        <input value={itinForm.cab_ref} onChange={e => setItinForm(f => ({ ...f, cab_ref: e.target.value }))} placeholder="E.g. CAB-54321" className={inputCls} />
                      </div>
                    </div>

                    {/* File Upload Option for Travel Desk (Multiple files supported) */}
                    <FileUpload
                      multiple
                      label="Upload Booking Bills / E-Tickets / Vouchers (PDF or Image)"
                      value={itinForm.file_urls}
                      onChange={urls => setItinForm(f => ({ ...f, file_urls: urls }))}
                      hint="Upload ticket PDFs, hotel vouchers, cab bills, or insurance documents"
                    />

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">Notes & Instructions</label>
                      <textarea rows={2} value={itinForm.notes} onChange={e => setItinForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes for employee..." className={`${inputCls} resize-none`} />
                    </div>
                    <div className="flex gap-2 justify-end pt-2">
                      {request.itinerary && (
                        <button type="button" onClick={() => setShowItinForm(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                          Cancel
                        </button>
                      )}
                      <button type="submit" disabled={actionLoading === 'itinerary'}
                        className="flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 shadow-xs">
                        {actionLoading === 'itinerary' ? <><Loader2 className="w-4 h-4 animate-spin" />Saving...</> : <><Upload className="w-4 h-4" />Save & Upload Itinerary</>}
                      </button>
                    </div>
                  </form>
                </CardContent>
              )}
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Actions */}
          <Card>
            <CardHeader><CardTitle className="text-sm">Actions</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {request.status === 'draft' && request.employee_id === user?.id && (
                <button onClick={handleSubmit} disabled={actionLoading === 'submit'}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60">
                  {actionLoading === 'submit' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submit for Approval
                </button>
              )}
              {isRole('manager', 'admin') && request.status === 'pending_approval' && (
                <>
                  <button onClick={() => setShowApproveModal(true)} disabled={!!actionLoading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-60 cursor-pointer">
                    <CheckCircle2 className="w-4 h-4" /> Approve Request
                  </button>
                  <button onClick={() => setShowRejectModal(true)} disabled={!!actionLoading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 cursor-pointer">
                    <XCircle className="w-4 h-4" /> Reject Request
                  </button>
                </>
              )}
              {request.status === 'settled' || (!['draft', 'pending_approval'].includes(request.status) && !isRole('manager', 'admin', 'travel_desk')) && (
                <p className="text-xs text-slate-400 text-center py-2">No actions available</p>
              )}
            </CardContent>
          </Card>

          {/* Approval Info */}
          {request.manager_name && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Approval</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <p className="text-xs text-slate-400">Manager</p>
                  <p className="font-medium text-slate-900">{request.manager_name}</p>
                </div>
                {request.manager_comment && (
                  <div>
                    <p className="text-xs text-slate-400">Comment</p>
                    <p className="text-slate-700">{request.manager_comment}</p>
                  </div>
                )}
                {request.approved_at && (
                  <div>
                    <p className="text-xs text-slate-400">Approved</p>
                    <p className="text-slate-700">{new Date(request.approved_at).toLocaleDateString('en-IN')}</p>
                  </div>
                )}
                {request.rejected_at && (
                  <div>
                    <p className="text-xs text-slate-400">Rejected</p>
                    <p className="text-slate-700">{new Date(request.rejected_at).toLocaleDateString('en-IN')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Cash Advance */}
          {request.cash_advance && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Cash Advance</CardTitle></CardHeader>
              <CardContent className="text-sm space-y-2">
                <div><p className="text-xs text-slate-400">Requested</p>
                  <p className="font-medium">₹{parseFloat(request.cash_advance.requested_amount).toLocaleString('en-IN')}</p>
                </div>
                <div><p className="text-xs text-slate-400">Status</p>
                  <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium capitalize", {
                    'bg-amber-100 text-amber-800': request.cash_advance.status === 'pending',
                    'bg-green-100 text-green-800': request.cash_advance.status === 'approved',
                    'bg-red-100 text-red-800': request.cash_advance.status === 'rejected',
                  })}>
                    {request.cash_advance.status}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Approve Confirmation Dialog */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" /> Approve Travel Request
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Are you sure you want to approve this request for {request.employee_name}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">
              Optional Comment / Notes
            </label>
            <textarea
              rows={3}
              value={approveComment}
              onChange={(e) => setApproveComment(e.target.value)}
              placeholder="E.g. Approved as per project budget allowance..."
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              onClick={() => setShowApproveModal(false)}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmApprove}
              disabled={actionLoading === 'approve'}
              className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold flex items-center gap-1.5"
            >
              {actionLoading === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Approval'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" /> Reject Travel Request
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Please provide a reason for rejecting this travel request.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1 uppercase tracking-wide">
              Rejection Reason (Required) *
            </label>
            <textarea
              rows={3}
              required
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="E.g. Budget ceiling exceeded, please reschedule to next month..."
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              onClick={() => setShowRejectModal(false)}
              className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmReject}
              disabled={actionLoading === 'reject'}
              className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold flex items-center gap-1.5"
            >
              {actionLoading === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject Request'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
